import React, { useState } from 'react'
import { Play, Clock, AlertCircle, CheckCircle, XCircle, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { evaluationService } from '@/services/evaluationService'
import type { TestSetWithDetails, EvaluationRunSummary, EvaluationRunDetail } from '@shared/types/evaluation'
import { toast } from 'sonner'

interface EvaluationRunnerProps {
  testSetId: string
  testSet: TestSetWithDetails
  onRunComplete?: (run: EvaluationRunSummary) => void
}

export const EvaluationRunner: React.FC<EvaluationRunnerProps> = ({ 
  testSetId, 
  testSet, 
  onRunComplete 
}) => {
  const [isRunning, setIsRunning] = useState(false)
  const [currentRun, setCurrentRun] = useState<EvaluationRunDetail | null>(null)
  const [passingScore, setPassingScore] = useState(0.7)
  const [showSettings, setShowSettings] = useState(false)

  const testCaseCount = testSet._count.testCases
  const hasTestCases = testCaseCount > 0

  const handleStartEvaluation = async () => {
    try {
      setIsRunning(true)
      
      // Start the evaluation run
      const run = await evaluationService.runEvaluation(testSetId, {
        passingScore
      })
      
      // Convert EvaluationRunSummary to EvaluationRunDetail for state
      const detailRun: EvaluationRunDetail = {
        ...run,
        testSetId: testSetId,
        runner: {
          firstName: run.runnerName.split(' ')[0] || '',
          lastName: run.runnerName.split(' ').slice(1).join(' ') || ''
        },
        evaluations: []
      }
      setCurrentRun(detailRun)
      toast.success('Evaluation started successfully')
      
      // Poll for updates (in a real app, you'd use WebSockets)
      const pollInterval = setInterval(async () => {
        try {
          const updatedRun = await evaluationService.getEvaluationRun(run.id)
          setCurrentRun(updatedRun)
          
          if (updatedRun.status === 'COMPLETED' || updatedRun.status === 'FAILED') {
            clearInterval(pollInterval)
            setIsRunning(false)
            
            if (updatedRun.status === 'COMPLETED') {
              toast.success('Evaluation completed successfully')
            } else {
              toast.error('Evaluation failed')
            }
            
            // Convert EvaluationRunDetail to EvaluationRunSummary for onRunComplete
            const summary: EvaluationRunSummary = {
              id: updatedRun.id,
              testSetName: updatedRun.testSetName,
              status: updatedRun.status,
              totalCases: updatedRun.totalCases,
              completedCases: updatedRun.completedCases,
              averageScore: updatedRun.averageScore,
              passedCases: updatedRun.passedCases,
              failedCases: updatedRun.failedCases,
              passingScore: updatedRun.passingScore,
              startedAt: updatedRun.startedAt,
              completedAt: updatedRun.completedAt,
              runnerName: `${updatedRun.runner.firstName} ${updatedRun.runner.lastName}`
            }
            onRunComplete?.(summary)
          }
        } catch (error) {
          console.error('Error polling evaluation status:', error)
          clearInterval(pollInterval)
          setIsRunning(false)
          toast.error('Failed to get evaluation status')
        }
      }, 2000)
      
    } catch (error) {
      console.error('Error starting evaluation:', error)
      toast.error('Failed to start evaluation')
      setIsRunning(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return <Clock className="h-4 w-4 text-blue-500" />
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'bg-blue-500'
      case 'COMPLETED':
        return 'bg-green-500'
      case 'FAILED':
        return 'bg-red-500'
      default:
        return 'bg-yellow-500'
    }
  }

  const progress = currentRun ? (currentRun.completedCases / currentRun.totalCases) * 100 : 0

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Run Evaluation</h3>
          <p className="text-sm text-muted-foreground">
            Execute evaluation against all test cases
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            Settings
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                disabled={!hasTestCases || isRunning}
                className="gap-2"
              >
                <Play className="h-4 w-4" />
                Start Evaluation
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Start Evaluation Run</AlertDialogTitle>
                <AlertDialogDescription>
                  This will run evaluation against all {testCaseCount} test cases in this test set.
                  The process may take several minutes to complete.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleStartEvaluation}>
                  Start Evaluation
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Evaluation Settings</CardTitle>
            <CardDescription>Configure parameters for the evaluation run</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="passingScore">Passing Score</Label>
                <Input
                  id="passingScore"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={passingScore}
                  onChange={(e) => setPassingScore(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum score (0-1) for a test case to be considered passing
                </p>
              </div>
              <div>
                <Label>Test Set Configuration</Label>
                <div className="text-sm text-muted-foreground mt-1">
                  <div>Model: {testSet.model}</div>
                  <div>Temperature: {testSet.temperature}</div>
                  <div>Test Cases: {testCaseCount}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Run Status */}
      {currentRun && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {getStatusIcon(currentRun.status)}
                Evaluation Run
              </CardTitle>
              <Badge 
                variant="secondary" 
                className={`${getStatusColor(currentRun.status)} text-white`}
              >
                {currentRun.status}
              </Badge>
            </div>
            <CardDescription>
              Started at {new Date(currentRun.startedAt).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{currentRun.completedCases} / {currentRun.totalCases}</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {currentRun.passedCases}
                </div>
                <div className="text-sm text-muted-foreground">Passed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {currentRun.failedCases}
                </div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {currentRun.averageScore?.toFixed(2) || '—'}
                </div>
                <div className="text-sm text-muted-foreground">Avg Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {currentRun.completedCases > 0 
                    ? ((currentRun.passedCases / currentRun.completedCases) * 100).toFixed(1) + '%'
                    : '—'
                  }
                </div>
                <div className="text-sm text-muted-foreground">Pass Rate</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Test Cases Warning */}
      {!hasTestCases && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Test Cases</h3>
              <p className="text-muted-foreground">
                Add test cases to this test set before running evaluations.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}